import React from "react";
import moment, { type Moment } from "moment";
import noop from "lodash/noop";
import partial from "lodash/partial";

import styles from "./cps-datepicker.styles.css";
import {
  granularityOptions,
  type GranularityOption,
} from "./cps-datepicker.helpers";
import Header from "./header.component";
import Days from "./days.component";
import FourByThree from "./four-by-three.component";

type LegacyEvent<T> = {
  detail: T;
};

type CpsCalendarProps = {
  date?: string | Date | Moment;
  endDate?: Moment;
  events?: {
    datechange?: (event: LegacyEvent<Date | null>) => void;
  };
  maxDate?: Date | Moment;
  minDate?: Date | Moment;
  removeDateOption?: boolean;
  removeDateText?: string;
  selectDate?: (date: Moment | null) => void;
  width?: number;
};

type CpsCalendarState = {
  granularity: GranularityOption;
  viewDate: Moment;
};

export default class CpsCalendar extends React.Component<
  CpsCalendarProps,
  CpsCalendarState
> {
  static defaultProps = {
    date: moment(),
    removeDateText: "Remove date",
    selectDate: noop,
    width: 264,
  };

  constructor(props: CpsCalendarProps) {
    super(props);

    this.state = {
      granularity: granularityOptions.DAY,
      viewDate: props.date ? moment(props.date) : moment(),
    };
  }

  componentDidUpdate(prevProps: CpsCalendarProps) {
    const format = "MM-DD-YYYY";
    if (
      this.props.endDate &&
      moment(this.props.endDate).format(format) !==
        moment(prevProps.endDate).format(format)
    ) {
      this.setState({ viewDate: moment(this.props.endDate) });
    } else if (
      this.props.date &&
      moment(this.props.date).format(format) !==
        moment(prevProps.date).format(format)
    ) {
      this.setState({ viewDate: moment(this.props.date) });
    }
  }

  render() {
    const {
      endDate,
      removeDateOption,
      removeDateText,
      width = 264,
      minDate,
      maxDate,
    } = this.props;
    const { viewDate, granularity } = this.state;

    return (
      <div style={{ width: `${width}px` }} className={styles.datepicker}>
        <div className={styles.datepickerBody}>
          <Header
            changeRange={this.changeRange}
            changeGranularity={this.changeGranularity}
            viewDate={viewDate}
            granularity={granularity}
            width={width}
          />
          {granularity === granularityOptions.DAY ? (
            <Days
              width={width}
              startDate={moment(this.props.date)}
              endDate={endDate}
              viewDate={viewDate}
              selectDate={this.selectDate}
              minDate={minDate}
              maxDate={maxDate}
            />
          ) : (
            <FourByThree
              width={width}
              pickRangeChangeDisplay={this.pickRangeChangeDisplay}
              granularity={granularity}
              viewDate={viewDate}
            />
          )}
        </div>
        {removeDateOption && (
          <div
            onClick={partial(this.selectDate, null)}
            className={`${styles.removeDateFooter} cps-color-primary cps-wt-bold`}
          >
            {removeDateText}
          </div>
        )}
      </div>
    );
  }

  selectDate = (date: Moment | null) => {
    const { events, selectDate = noop } = this.props;
    if (events) {
      events.datechange?.({
        detail: date ? new Date(date.toString()) : null,
      });
    } else {
      selectDate(date);
    }
  };

  changeGranularity = (granularity: GranularityOption) => {
    this.setState({
      granularity,
    });
  };

  changeRange = (modifier: number) => {
    this.setState((prevState) => ({
      viewDate: getRangeDate(
        prevState.granularity,
        modifier,
        prevState.viewDate,
      ),
    }));
  };

  pickRangeChangeDisplay = (
    range: number | string,
    granularity: GranularityOption,
  ) => {
    const newDate = moment(this.state.viewDate);

    newDate[
      this.state.granularity === granularityOptions.MONTH
        ? granularityOptions.MONTH
        : granularityOptions.YEAR
    ](range as any);

    this.setState({
      granularity,
      viewDate: newDate,
    });
  };
}

function getRangeDate(
  subject: GranularityOption,
  modifier: number,
  oldDate: Moment,
): Moment {
  const date = moment(oldDate);

  if (subject === granularityOptions.DAY)
    return date.month(oldDate.month() + modifier);
  if (subject === granularityOptions.MONTH)
    return date.year(oldDate.year() + modifier);
  if (subject === granularityOptions.YEAR)
    return date.year(oldDate.year() + modifier * 10);
  if (subject === granularityOptions.DECADE)
    return date.year(oldDate.year() + modifier * 100);

  return date; // fallback
}

export const CprCalendar = CpsCalendar;
